/**
 * Fee Schedule Tab
 *
 * Displays and manages fee schedules for a company with tiered pricing support.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Layers,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { GroupedFeeTiers, PricingType } from '@/types/title-escrow';
import { titleEscrowService } from '@/services/titleEscrowService';
import { AddFeeModal } from './AddFeeModal';
import { EditFeeModal } from './EditFeeModal';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('FeeScheduleTab', 'title-escrow');

interface FeeScheduleTabProps {
  companyId: string;
  companyName: string;
}

export const FeeScheduleTab: React.FC<FeeScheduleTabProps> = ({
  companyId,
  companyName
}) => {
  const [fees, setFees] = useState<GroupedFeeTiers[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFees, setExpandedFees] = useState<Set<string>>(new Set());
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFee, setEditingFee] = useState<GroupedFeeTiers | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<GroupedFeeTiers | null>(null);

  const loadFees = async () => {
    try {
      setLoading(true);
      const data = await titleEscrowService.getGroupedFeeSchedule(companyId);
      setFees(data);
    } catch (error) {
      log.error('Failed to load fees', { companyId }, error as Error);
      toast.error('Failed to load fee schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFees();
  }, [companyId]);

  const handleDelete = async (fee: GroupedFeeTiers) => {
    try {
      await titleEscrowService.removeAllFeeTiers(companyId, fee.feeId);
      toast.success('Fee removed from schedule');
      loadFees();
    } catch (error) {
      log.error('Failed to remove fee', { feeId: fee.feeId }, error as Error);
      toast.error('Failed to remove fee');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const toggleExpanded = (feeId: string) => {
    setExpandedFees((prev) => {
      const next = new Set(prev);
      if (next.has(feeId)) {
        next.delete(feeId);
      } else {
        next.add(feeId);
      }
      return next;
    });
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null) {
return '—';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatLoanAmount = (amount: number | null) => {
    if (amount === null) {
return 'No limit';
}
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPricingTypeBadge = (type: PricingType) => {
    switch (type) {
      case 'fixed':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Fixed</Badge>;
      case 'tiered':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Tiered</Badge>;
      case 'percentage':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Percentage</Badge>;
    }
  };

  const filteredFees = fees.filter((fee) =>
    fee.feeName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Fee Schedule</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {companyName} - {fees.length} fee{fees.length !== 1 ? 's' : ''} configured
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Fee
          </Button>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative w-64 mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search fees..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Fee List */}
          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32 mt-2" />
                  </div>
                  <Skeleton className="h-8 w-24" />
                </div>
              ))}
            </div>
          ) : filteredFees.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No fees configured</p>
              <p className="text-sm mt-1">
                Add fees from the Encompass fee library to build this company&apos;s fee schedule.
              </p>
              <Button variant="outline" className="mt-4" onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Fee
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead>Fee Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount / Tiers</TableHead>
                  <TableHead>Sections</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFees.map((fee) => (
                  <React.Fragment key={fee.feeId}>
                    <TableRow className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        {fee.pricingType === 'tiered' && fee.tiers.length > 1 ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => toggleExpanded(fee.feeId)}
                          >
                            {expandedFees.has(fee.feeId) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        ) : null}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {fee.feeName}
                          {fee.isConditional && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              Conditional
                            </Badge>
                          )}
                        </div>
                        {fee.conditionDescription && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {fee.conditionDescription}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>{getPricingTypeBadge(fee.pricingType)}</TableCell>
                      <TableCell>
                        {fee.pricingType === 'tiered' ? (
                          <div className="flex items-center gap-2">
                            <Layers className="w-4 h-4 text-muted-foreground" />
                            <span>{fee.tiers.length} tier{fee.tiers.length !== 1 ? 's' : ''}</span>
                          </div>
                        ) : fee.pricingType === 'percentage' ? (
                          <span>{(fee.tiers[0]?.percentageRate || 0) * 100}%</span>
                        ) : (
                          formatCurrency(fee.tiers[0]?.amount || null)
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {fee.sections.map((section) => (
                            <Badge key={section} variant="secondary" className="text-xs">
                              {section.replace('Section', '')}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingFee(fee)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => setDeleteConfirm(fee)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    {/* Expanded tiers for tiered pricing */}
                    {fee.pricingType === 'tiered' && expandedFees.has(fee.feeId) && (
                      <TableRow className="bg-muted/30">
                        <TableCell colSpan={6} className="py-2">
                          <div className="ml-10 space-y-1">
                            {fee.tiers.map((tier, idx) => (
                              <div
                                key={tier.id}
                                className="flex items-center gap-4 text-sm py-1"
                              >
                                <span className="text-muted-foreground w-24">
                                  {formatLoanAmount(tier.minLoanAmount)} -
                                </span>
                                <span className="text-muted-foreground w-24">
                                  {formatLoanAmount(tier.maxLoanAmount)}:
                                </span>
                                <span className="font-medium">
                                  {formatCurrency(tier.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Fee Modal */}
      <AddFeeModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        companyId={companyId}
        existingFeeIds={fees.map((f) => f.feeId)}
        onFeeAdded={() => {
          loadFees();
          setShowAddModal(false);
        }}
      />

      {/* Edit Fee Modal */}
      <EditFeeModal
        open={!!editingFee}
        onOpenChange={(open) => !open && setEditingFee(null)}
        companyId={companyId}
        fee={editingFee}
        onFeeUpdated={() => {
          loadFees();
          setEditingFee(null);
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Fee?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove &quot;{deleteConfirm?.feeName}&quot; and all its pricing tiers from
              this company&apos;s fee schedule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FeeScheduleTab;
